import { HttpClient, HttpErrorResponse, HttpHeaders, HttpRequest, HttpResponse
       } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ProductDesignerService } from 'app/product-designer.service';
import { ConfigParserError } from 'core/config/config-parser';
import { ConfigService } from 'core/config/config.service';
import { TimeoutError } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { FontDescription } from '../../text/text.model';
import { Utils } from '../utils';
import { FontMetaDataConfigParser } from './font-metadata-config-parser';

const DEFAULT_FONT_TIMEOUT = 30; // in seconds

export interface FontStoreRequestError
{
  status: number;
  statusText: string;
  detail?: string;
}

@Injectable()
export class FontStoreService
{
  private fontStoreDomain: string;

  constructor(private http: HttpClient,
              private productDesignerService: ProductDesignerService,
              @Inject(ConfigService) private configService: ConfigService,
  )
  {
    this.fontStoreDomain =
        this.configService.getProductDesignerComponentConfig().fontStoreURL;
  }

  public getFontPreviewUrl(fontName: string): string
  {
    return this.fontStoreDomain + '/fonts/' + fontName + '/preview';
  }

  /**
   * @throws FontStoreRequestError
   */
  public async getFonts(): Promise<Array<FontDescription>>
  {
    const fontsUrl = this.fontStoreDomain + '/fonts/';
    const req = new HttpRequest('GET', fontsUrl + '?include_metadata=true', {
        headers: new HttpHeaders()
    });

    try {
      const resp = <any> await this.http.request(req)
        .pipe(Utils.retryPipe(2000))
        .toPromise()
        .catch((exc) => {
          throw exc;
        });

      if (resp instanceof HttpResponse) {
        if (resp.status === 200) {
          let fonts = resp.body['fonts']
              .sort((a, b) => a.name.localeCompare(b.name));

          fonts.forEach((f, i) => {
            const configParser = new FontMetaDataConfigParser(f);
            fonts[i] = {
              name: f.name,
              url: fontsUrl + encodeURIComponent(f.name)
                   + '?bold=' + !!f.bold + '&italic=' + !!f.italic,
              group: f.group,
              bold: !!f.bold,
              italic: !!f.italic,
              unicodeRanges: configParser.getUnicodeRanges(),
              charDimensions: configParser.getCharDimensions(),
              normalizeFactor: configParser.getNormalizeFactor(),
            };
          });
          return fonts;
        }
        else {
          throw this.buildFontStoreRequestError(resp, fontsUrl);
        }
      }
    }
    catch (error) {
      throw this.buildFontStoreRequestError(error, fontsUrl);
    }
  }

  /**
   * @throws FontStoreRequestError
   */
  public async loadFont(
      font: FontDescription,
      fontTimeout: number = DEFAULT_FONT_TIMEOUT): Promise<void>
  {
    this.productDesignerService.suspendDesignerTrigger.emit({suspend: true});
    try {
      const resp = await this.http.get(font.url, { responseType: 'blob' })
        .pipe(
        timeout(fontTimeout * 1000))
        .pipe(Utils.retryPipe())
        .toPromise()
        .catch(e => {
          throw e;
        });
      if (! (resp instanceof Blob)) {
        throw resp;
      }
      this.productDesignerService.suspendDesignerTrigger.emit({suspend: false});
    }
    catch (e) {
      this.productDesignerService.suspendDesignerTrigger.emit({suspend: false});
      throw this.buildFontStoreRequestError(e, font.url);
    }
  }

  private buildFontStoreRequestError(error, url: string):
      FontStoreRequestError
  {
    let _error: FontStoreRequestError;
    if (error instanceof HttpErrorResponse) {
      if (error.status === 404) {
          _error = <FontStoreRequestError> {
              status: 404,
              statusText: 'Not Found',
              detail: url
          };
      }
      else {
        _error = <FontStoreRequestError> {
            status: error.status,
            statusText: error.statusText,
            detail: error.error['detail']
        };
      }
    }
    else if (error instanceof TimeoutError) {
        _error = <FontStoreRequestError> {
          status: 499,
          statusText: 'Client Closed Request (TimeoutError)',
          detail: error.stack
        };
    }
    else if (error instanceof ConfigParserError) {
      _error = <FontStoreRequestError> {
          status: <any>error.name,
          statusText: error.reference.join(' '),
          detail: error.message
      };
    }
    else {
      _error = <FontStoreRequestError> {
          status: error.status,
          statusText: error.statusText,
          detail: 'undefined Reason'
      };
    }
    return _error;
  }
}
